// Slide styles (issue 32): a few page layouts in relative units (fractions of the artboard, like
// BUILTIN_TEMPLATES in app/services/content.py), each with image slots and text roles. "Add slide"
// builds an artboard from one; "Change style" re-flows an existing artboard into one without
// losing anything: what the new style has no place for is kept as a hidden layer.
import type { Artboard, BrandSpec, ImageLayer, Layer, Layout, LogoLayer, ShapeLayer, StyleName, TextLayer } from '../../lib/contentTypes';

interface PartBase {
  x: number;
  y: number;
  w: number;
  h: number;
}
interface ImagePart extends PartBase {
  t: 'image';
  name: string;
}
interface TextPart extends PartBase {
  t: 'text';
  role: StyleName;
  color: string; // a brand colour name
  align?: TextLayer['align'];
  valign?: TextLayer['valign'];
  /** px size as a percentage of the artboard width, instead of the brand style's size */
  sizePct?: number;
}
interface ShapePart extends PartBase {
  t: 'shape';
  shape: ShapeLayer['shape'];
  color: string;
}
interface LogoPart extends PartBase {
  t: 'logo';
}
type Part = ImagePart | TextPart | ShapePart | LogoPart;

export interface SlideStyle {
  key: string;
  name: string;
  description: string;
  background: string; // a brand colour name
  parts: Part[];
}

const img = (x: number, y: number, w: number, h: number, name = 'Image'): ImagePart => ({ t: 'image', x, y, w, h, name });
const txt = (role: StyleName, x: number, y: number, w: number, h: number, color: string, more: Partial<TextPart> = {}): TextPart => ({ t: 'text', role, x, y, w, h, color, ...more });
const accent = (x: number, y: number): ShapePart => ({ t: 'shape', shape: 'square', x, y, w: 0.022, h: 0.022, color: 'Bronze' });

export const SLIDE_STYLES: SlideStyle[] = [
  {
    key: 'image-led',
    name: 'Image-led',
    description: 'One image, full bleed, with a discreet title and credit.',
    background: 'Ink',
    parts: [img(0, 0, 1, 1), txt('subtitle', 0.06, 0.865, 0.88, 0.05, 'White'), txt('caption', 0.06, 0.925, 0.88, 0.035, 'White')],
  },
  {
    key: 'editorial',
    name: 'Editorial',
    description: 'Asymmetric: a wide left margin, the image pushed right, a strong title below.',
    background: 'Paper',
    parts: [
      img(0.3, 0.06, 0.64, 0.6),
      txt('label', 0.06, 0.075, 0.2, 0.04, 'Bronze'),
      accent(0.06, 0.675),
      txt('title', 0.06, 0.71, 0.88, 0.15, 'Ink'),
      txt('caption', 0.06, 0.89, 0.6, 0.05, 'Ink'),
    ],
  },
  {
    key: 'typographic',
    name: 'Typographic',
    description: 'A large headline with one image under it.',
    background: 'Ink',
    parts: [txt('title', 0.06, 0.07, 0.88, 0.36, 'White', { sizePct: 10.5 }), img(0.06, 0.5, 0.88, 0.38), txt('caption', 0.06, 0.915, 0.88, 0.04, 'Paper')],
  },
  {
    key: 'modular',
    name: 'Modular',
    description: 'One dominant image and two detail images.',
    background: 'Paper',
    parts: [img(0.06, 0.06, 0.88, 0.56, 'Main image'), img(0.06, 0.645, 0.43, 0.22, 'Detail 1'), img(0.51, 0.645, 0.43, 0.22, 'Detail 2'), txt('caption', 0.06, 0.9, 0.88, 0.04, 'Ink')],
  },
  {
    key: 'comparison',
    name: 'Comparison',
    description: 'Two aligned images with a label under each.',
    background: 'Ink',
    parts: [
      img(0, 0, 0.497, 0.88, 'Left image'),
      img(0.503, 0, 0.497, 0.88, 'Right image'),
      txt('label', 0.06, 0.915, 0.38, 0.04, 'White'),
      txt('label', 0.56, 0.915, 0.38, 0.04, 'White'),
    ],
  },
  {
    key: 'text',
    name: 'Text',
    description: 'A title and a paragraph, no image.',
    background: 'Paper',
    parts: [
      accent(0.06, 0.1),
      txt('title', 0.06, 0.14, 0.88, 0.14, 'Ink'),
      txt('body', 0.06, 0.32, 0.88, 0.4, 'Ink'),
      { t: 'shape', shape: 'line', x: 0.06, y: 0.8, w: 0.88, h: 0.003, color: 'Ink' },
    ],
  },
  {
    key: 'closing',
    name: 'Closing',
    description: 'The logo and one closing line.',
    background: 'Ink',
    parts: [
      { t: 'logo', x: 0.3, y: 0.36, w: 0.4, h: 0.12 },
      txt('subtitle', 0.1, 0.55, 0.8, 0.08, 'White', { align: 'center' }),
      txt('caption', 0.1, 0.65, 0.8, 0.05, 'Paper', { align: 'center' }),
    ],
  },
];

const PLACEHOLDER: Record<StyleName, string> = {
  title: 'Title',
  subtitle: 'Subtitle',
  body: 'Write the text of this slide here.',
  caption: 'Caption or credit',
  label: 'Label',
};
const FALLBACK: Record<string, string> = { Ink: '#1c1b19', Paper: '#f5f4f0', Bronze: '#8a5a32', White: '#ffffff' };
/** Marks the decoration a style draws itself: "Change style" replaces it instead of hiding it. */
const DECOR = 'Style · ';

type MakeId = (prefix: string) => string;

function colorOf(brand: BrandSpec, name: string): string {
  return brand.colors.find((c) => c.name === name)?.hex ?? FALLBACK[name] ?? '#1c1b19';
}

function geometry(p: PartBase, layout: Layout) {
  const r = (v: number) => Math.round(v * 100) / 100;
  return { x: r(p.x * layout.width), y: r(p.y * layout.height), w: r(p.w * layout.width), h: r(p.h * layout.height) };
}

function textFields(p: TextPart, layout: Layout, brand: BrandSpec) {
  return {
    style: p.role,
    color: colorOf(brand, p.color),
    align: p.align ?? 'left',
    valign: p.valign ?? 'top',
    size: p.sizePct ? Math.round((p.sizePct / 100) * layout.width) : null,
    font: null,
    weight: null,
  };
}

function freshLayer(p: Part, layout: Layout, brand: BrandSpec, id: string): Layer {
  const g = geometry(p, layout);
  if (p.t === 'image') {
    return { type: 'image', id, name: p.name, ...g, asset_id: null, fit: 'fill', focal_x: 0.5, focal_y: 0.5, crop: { x: 0, y: 0, w: 1, h: 1 } } satisfies ImageLayer;
  }
  if (p.t === 'text') return { type: 'text', id, ...g, text: PLACEHOLDER[p.role], ...textFields(p, layout, brand) } as TextLayer;
  if (p.t === 'shape') return { type: 'shape', id, name: `${DECOR}${p.shape}`, ...g, shape: p.shape, color: colorOf(brand, p.color) } satisfies ShapeLayer;
  return { type: 'logo', id, name: 'Logo', ...g } satisfies LogoLayer;
}

/** A new artboard in `style`: empty image slots, placeholder texts. */
export function buildArtboard(style: SlideStyle, layout: Layout, brand: BrandSpec, mk: MakeId): Artboard {
  return {
    id: mk('a'),
    name: style.name,
    background: colorOf(brand, style.background),
    layers: style.parts.map((p) => freshLayer(p, layout, brand, mk('l'))),
  };
}

/** Top-to-bottom, then left-to-right; layers within 3% of the height of each other count as one row. */
function readingOrder<T extends Layer>(layers: T[], layout: Layout): T[] {
  const band = layout.height * 0.03;
  return [...layers].sort((a, b) => (Math.abs(a.y - b.y) < band ? a.x - b.x : a.y - b.y));
}

/** Re-flows `ab` into `style`. Images fill the image slots in reading order (asset, crop and focal
 *  point kept); texts fill the text roles by style, then the remaining roles in reading order; the
 *  first logo takes the logo place. Whatever has no place is kept as a hidden layer (never deleted);
 *  only the decoration an earlier style drew itself is replaced. */
export function restyle(ab: Artboard, style: SlideStyle, layout: Layout, brand: BrandSpec, mk: MakeId): { artboard: Artboard; hidden: number } {
  const old = ab.layers.filter((l) => !(l.type === 'shape' && (l.name ?? '').startsWith(DECOR)));
  const images = readingOrder(old.filter((l): l is ImageLayer => l.type === 'image' && !l.hidden), layout);
  const texts = readingOrder(old.filter((l): l is TextLayer => l.type === 'text' && !l.hidden), layout);
  const logos = readingOrder(old.filter((l): l is LogoLayer => l.type === 'logo' && !l.hidden), layout);
  const placed = new Set<string>();
  const out: Layer[] = [];

  const textParts = style.parts.filter((p): p is TextPart => p.t === 'text');
  const filled = new Map<TextPart, TextLayer>();
  for (const p of textParts) {
    const hit = texts.find((t) => !placed.has(t.id) && t.style === p.role);
    if (hit) {
      filled.set(p, hit);
      placed.add(hit.id);
    }
  }
  for (const p of textParts) {
    if (filled.has(p)) continue;
    const hit = texts.find((t) => !placed.has(t.id));
    if (hit) {
      filled.set(p, hit);
      placed.add(hit.id);
    }
  }

  let imageAt = 0;
  let logoAt = 0;
  for (const p of style.parts) {
    const g = geometry(p, layout);
    if (p.t === 'image') {
      const prev = images[imageAt++];
      if (!prev) out.push(freshLayer(p, layout, brand, mk('l')));
      else {
        placed.add(prev.id);
        out.push({ ...prev, ...g, fit: 'fill', hidden: false, span: false });
      }
    } else if (p.t === 'text') {
      const prev = filled.get(p);
      if (!prev) out.push(freshLayer(p, layout, brand, mk('l')));
      else out.push({ ...prev, ...g, ...textFields(p, layout, brand), hidden: false, span: false });
    } else if (p.t === 'logo') {
      const prev = logos[logoAt++];
      if (!prev) out.push(freshLayer(p, layout, brand, mk('l')));
      else {
        placed.add(prev.id);
        out.push({ ...prev, ...g, hidden: false, span: false });
      }
    } else out.push(freshLayer(p, layout, brand, mk('l')));
  }

  // everything without a place stays, hidden, under the new layers
  const kept = old.filter((l) => !placed.has(l.id) || l.hidden).map((l) => ({ ...l, hidden: true }) as Layer);
  const hiddenNew = kept.filter((l) => !old.find((o) => o.id === l.id)?.hidden).length;
  return { artboard: { ...ab, background: colorOf(brand, style.background), layers: [...kept, ...out] }, hidden: hiddenNew };
}
